import { useEffect, useMemo, useState } from "react";
import { Trophy, RefreshCw, Loader2, Award, TrendingUp, Users } from "lucide-react";
import { Card } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { adminApi } from "../../lib/api";
import { formatCOP } from "../../lib/api";
import { toast } from "sonner";

const PERIODS = [
  { value: "week", label: "Última semana" },
  { value: "month", label: "Último mes" },
  { value: "all", label: "Todo" },
];

const formatDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("es-CO", { day: "2-digit", month: "short" });
  } catch {
    return iso;
  }
};

const MEDALS = ["🥇", "🥈", "🥉"];

export default function AdminVendedores() {
  const [period, setPeriod] = useState("month");
  const [data, setData] = useState({ items: [], total_amount: 0, total_orders: 0 });
  const [loading, setLoading] = useState(true);

  const fetchRanking = async (p = period) => {
    setLoading(true);
    try {
      const { data } = await adminApi.get(`/admin/vendedores-ranking?period=${p}`);
      setData(data || { items: [], total_amount: 0, total_orders: 0 });
    } catch {
      toast.error("No pudimos cargar el ranking");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await adminApi.get(`/admin/vendedores-ranking?period=${period}`);
        if (!cancelled) setData(data || { items: [], total_amount: 0, total_orders: 0 });
      } catch {
        if (!cancelled) toast.error("No pudimos cargar el ranking");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [period]);

  const maxAmount = useMemo(
    () => Math.max(...(data.items || []).map((i) => i.monto_total || 0), 1),
    [data.items]
  );

  return (
    <div data-testid="admin-vendedores-page">
      <div className="mb-6 flex items-start justify-between gap-4 flex-wrap">
        <div>
          <p className="text-xs uppercase tracking-widest text-cm-ink-soft font-medium">
            Ranking
          </p>
          <h1 className="font-serif text-3xl mt-1 tracking-tight flex items-center gap-3">
            <Trophy className="h-7 w-7 text-cm-yellow" />
            Vendedores del portal B2B
          </h1>
          <p className="text-sm text-cm-ink-soft mt-2 max-w-2xl">
            Top vendedores por monto facturado a través del portal. Solo cuenta los pedidos
            realizados por los clientes en línea.
          </p>
        </div>
        <Button
          variant="outline"
          onClick={() => fetchRanking(period)}
          className="border-cm-border"
          data-testid="refresh-ranking-btn"
        >
          <RefreshCw className={`h-4 w-4 mr-2 ${loading ? "animate-spin" : ""}`} />
          Actualizar
        </Button>
      </div>

      <div className="flex gap-2 mb-6" data-testid="period-tabs">
        {PERIODS.map((p) => (
          <button
            key={p.value}
            type="button"
            onClick={() => setPeriod(p.value)}
            data-testid={`period-${p.value}`}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              period === p.value
                ? "bg-cm-ink text-cm-cream"
                : "bg-white border border-cm-border text-cm-ink-soft hover:text-cm-ink"
            }`}
          >
            {p.label}
          </button>
        ))}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
        <Card className="p-5 bg-white border-cm-border shadow-none">
          <div className="flex items-center gap-2 text-cm-ink-soft text-xs uppercase tracking-widest">
            <Users className="h-3.5 w-3.5" /> Vendedores activos
          </div>
          <p className="font-mono text-3xl font-bold mt-2">{data.items?.length || 0}</p>
        </Card>
        <Card className="p-5 bg-white border-cm-border shadow-none">
          <div className="flex items-center gap-2 text-cm-ink-soft text-xs uppercase tracking-widest">
            <TrendingUp className="h-3.5 w-3.5" /> Pedidos del período
          </div>
          <p className="font-mono text-3xl font-bold mt-2">{data.total_orders || 0}</p>
        </Card>
        <Card className="p-5 bg-cm-ink text-cm-cream border-0 shadow-none">
          <div className="flex items-center gap-2 text-cm-cream/70 text-xs uppercase tracking-widest">
            <Award className="h-3.5 w-3.5" /> Monto facturado
          </div>
          <p className="font-mono text-3xl font-bold mt-2 text-cm-yellow">
            {formatCOP(data.total_amount || 0)}
          </p>
        </Card>
      </div>

      {loading ? (
        <Card className="p-12 text-center bg-white border-cm-border shadow-none">
          <Loader2 className="h-8 w-8 mx-auto animate-spin text-cm-ink-soft" />
          <p className="text-sm text-cm-ink-soft mt-3">Calculando ranking...</p>
        </Card>
      ) : !data.items || data.items.length === 0 ? (
        <Card className="p-12 text-center bg-white border-cm-border shadow-none">
          <Trophy className="h-12 w-12 mx-auto text-cm-ink-soft/40" />
          <h2 className="font-serif text-xl mt-4">Sin pedidos en este período</h2>
          <p className="text-sm text-cm-ink-soft mt-2">
            Aún no hay pedidos del portal en este período. Cambia el filtro o espera nuevos pedidos.
          </p>
        </Card>
      ) : (
        <div className="space-y-2" data-testid="ranking-list">
          {data.items.map((v, idx) => {
            const percent = (v.monto_total / maxAmount) * 100;
            return (
              <Card
                key={`${v.vendedor_id || "none"}-${idx}`}
                className="bg-white border-cm-border shadow-none overflow-hidden"
                data-testid={`ranking-row-${idx + 1}`}
              >
                <div className="flex items-center gap-4 px-5 py-4">
                  <div className="w-10 flex-shrink-0 text-center">
                    {idx < 3 ? (
                      <span className="text-2xl" title={`#${idx + 1}`}>
                        {MEDALS[idx]}
                      </span>
                    ) : (
                      <span className="font-mono text-cm-ink-soft font-medium">
                        #{idx + 1}
                      </span>
                    )}
                  </div>

                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="font-medium text-cm-ink truncate">{v.nombre}</p>
                      {v.cargo && (
                        <span className="text-[10px] uppercase tracking-widest text-cm-ink-soft">
                          · {v.cargo}
                        </span>
                      )}
                    </div>
                    <div className="mt-2 h-1.5 bg-cm-cream-alt rounded-full overflow-hidden">
                      <div
                        className="h-full bg-gradient-to-r from-cm-yellow to-cm-red transition-all"
                        style={{ width: `${percent}%` }}
                      />
                    </div>
                    <div className="mt-1.5 flex items-center gap-3 text-[11px] text-cm-ink-soft">
                      <span>
                        {v.pedidos} pedido{v.pedidos === 1 ? "" : "s"}
                      </span>
                      <span>·</span>
                      <span>{v.items_total} líneas</span>
                      <span>·</span>
                      <span>Último: {formatDate(v.ultimo_pedido)}</span>
                    </div>
                  </div>

                  <div className="text-right flex-shrink-0">
                    <p className="font-mono text-lg font-bold text-cm-ink">
                      {formatCOP(v.monto_total)}
                    </p>
                    <p className="text-[10px] uppercase tracking-widest text-cm-ink-soft">
                      Total facturado
                    </p>
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
